"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface VerificationCodeInputProps {
  /** Used as fallback when `description` is not provided. */
  email?: string;
  /** Custom description shown above the code input. */
  description?: string;
  isPending: boolean;
  error?: { message?: string } | null;
  onSubmit: (code: string) => void;
  onBack: () => void;
}

export function VerificationCodeInput({
  email,
  description,
  isPending,
  error,
  onSubmit,
  onBack,
}: VerificationCodeInputProps) {
  const t = useTranslations("auth");
  const [code, setCode] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) {
      setValidationError("codeRequired");
      return;
    }
    setValidationError(null);
    onSubmit(code.trim());
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <p className="text-base text-muted-foreground">
        {description ?? t("verifyDescription", { email: email ?? "" })}
      </p>

      <div className="space-y-2">
        <Label htmlFor="verification-code" className="text-base">
          {t("verificationCode")}
        </Label>
        <Input
          id="verification-code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={t("verificationCodePlaceholder")}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="h-14 text-center text-3xl tracking-[0.3em] placeholder:text-lg placeholder:tracking-normal placeholder:leading-[3.5rem]"
          aria-invalid={!!(validationError || error)}
        />
        {validationError && (
          <p className="text-sm text-destructive">{t(validationError)}</p>
        )}
        {error && (
          <p className="text-sm text-destructive">{error.message}</p>
        )}
      </div>

      <Button className="h-12 w-full text-base" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="size-5 animate-spin" />
            {t("verifying")}
          </>
        ) : (
          t("verify")
        )}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="h-12 w-full gap-2 text-base"
        onClick={onBack}
        disabled={isPending}
      >
        <ArrowLeft className="size-5" />
        {t("backButton")}
      </Button>
    </form>
  );
}
